import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Pencil, Check, X, RotateCcw } from "lucide-react";

/**
 * Inline KPI editor for the executive dashboard. Values are edited in Crores
 * (exec-friendly) and stored back in absolute rupees. Supports a sticky manual
 * override with a reset-to-auto action.
 *
 * Props:
 *  - value: effective numeric value (absolute INR)
 *  - display: rendered node for the current value (e.g. <Money .../>)
 *  - overridden: boolean (manual override active)
 *  - canEdit: boolean
 *  - onSave: async (absoluteValue:number) => void
 *  - onReset: async () => void
 *  - testId: string
 */
export function KpiValueEditor({ value, display, overridden = false, canEdit = false, onSave, onReset, testId }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const start = () => { setDraft(((Number(value) || 0) / 1e7).toString()); setEditing(true); };
  const cancel = () => { setEditing(false); };
  const save = async () => {
    const cr = parseFloat(draft);
    if (isNaN(cr)) { toast.error("Enter the amount in Crores"); return; }
    setSaving(true);
    try {
      await onSave(Math.round(cr * 1e7 * 100) / 100);
      toast.success("KPI updated");
      setEditing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save the value");
    } finally { setSaving(false); }
  };
  const reset = async () => {
    setSaving(true);
    try { await onReset(); toast.success("Reset to auto-calculated value"); }
    catch (e) { toast.error("Failed to reset"); }
    finally { setSaving(false); }
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-1" data-testid={`${testId}-editor`}>
        <span className="text-base text-muted-foreground">₹</span>
        <Input
          type="number" step="0.01" value={draft} autoFocus
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") cancel(); }}
          className="h-9 w-28 text-right text-base font-semibold tabular-nums"
          data-testid={`${testId}-input`}
        />
        <span className="text-xs text-muted-foreground">Cr</span>
        <Button size="icon" variant="ghost" className="h-8 w-8 p-0 text-[hsl(var(--success))]" onClick={save} disabled={saving} data-testid={`${testId}-save`}><Check className="h-4 w-4" /></Button>
        <Button size="icon" variant="ghost" className="h-8 w-8 p-0 text-muted-foreground" onClick={cancel} disabled={saving} data-testid={`${testId}-cancel`}><X className="h-4 w-4" /></Button>
      </span>
    );
  }

  return (
    <span className="group/kpi inline-flex items-center gap-1.5">
      {display}
      {overridden && (
        <Badge variant="outline" className="border-[hsl(var(--warning)/0.4)] bg-[hsl(var(--warning)/0.10)] px-1.5 py-0 text-[9px] uppercase tracking-wide text-[hsl(var(--warning))]" data-testid={`${testId}-manual-badge`}>Manual</Badge>
      )}
      {canEdit && (
        <Button size="icon" variant="ghost" className="h-7 w-7 p-0 opacity-40 transition-opacity duration-150 hover:opacity-100 group-hover/kpi:opacity-80" onClick={start} data-testid={`${testId}-edit`}>
          <Pencil className="h-3.5 w-3.5" />
        </Button>
      )}
      {overridden && canEdit && (
        <Button size="icon" variant="ghost" className="h-7 w-7 p-0 opacity-60 hover:opacity-100" onClick={reset} title="Reset to auto-calculated value" data-testid={`${testId}-reset`}>
          <RotateCcw className="h-3.5 w-3.5" />
        </Button>
      )}
    </span>
  );
}
